import React, { useContext, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { useSnackbar } from "../../contexts/InfoContext";
import { useAuth } from "../../contexts/AuthContext";
import useUserClaims from "../../hooks/useUserClaims";
import getInternationalDate from "../../utils/getDate";
import CustomTextField from "../Credit/component/CustomTextField";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import getRequiredUserData from "../../utils/getBranchInfo";
import { BankAccountInfoFormValidationSchema } from "./account_info_validation";
import createBankAccountInformation from "../../api/bank/create_account_information";

const BankAccountInformationForm = ({ source }) => {
  const params = useParams();
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const userClaims = useUserClaims(user);
  const theme = useTheme();
  const userData = getRequiredUserData();
  const { setIsSubmitting } = useContext(SpinnerContext);

  const [bank, setBank] = useState([
    "Commercial Bank of Ethiopia (CBE)",
    "Bank of Abyssinia",
    "Awash Bank",
    "Abay Bank",
    "Addis International Bank",
    "Amhara Bank",
    "Berhan Bank",
    "Bunna Bank",
    "Cooperative Bank of Oromia",
    "Dashen Bank",
    "Debub Global Bank",
    "Enat Bank",
    "Hibret Bank",
    "Hijra Bank",
    "Lion Bank",
    "Nib Bank",
    "Oromia Bank",
    "Wegagen Bank",
    "ZamZam Bank",
    "Zemen Bank",
    "Shabelle Bank",
    "Ahadu Bank",
    "Siinqee Bank",
    "Tsehay Bank",
  ]);

  const handleButtonClick = () => {
    setShowForm(true);
  };

  const formik = useFormik({
    initialValues: {
      bankName: "",
      accountNumber: "",
    },

    validationSchema: BankAccountInfoFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();
        values.branchId = params.id;
        values.branchId = userClaims.finance
          ? user.uid
          : params.id
          ? params.id
          : user.displayName;

        if (!values.branchId) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Branch information is not found. Please check your connection, refresh your browser, and try again.",
                type: "error",
              },
            },
          };
        }

        values.date = date;

        const res = await createBankAccountInformation(user, values);
        openSnackbar(`${res.data.message} successfully created!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred.Please kindly check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  const handleCloseForm = () => {
    setShowForm(false);
    formik.resetForm();
  };

  return (
    <div>
      {userClaims.finance || userClaims.admin ? (
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleButtonClick}
          >
            Create New Bank Account
          </Button>

          <Dialog
            open={showForm}
            onClose={handleCloseForm}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
              New Bank Account Information
            </DialogTitle>
            <DialogContent
              sx={{ backgroundColor: theme.palette.background.alt }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <TextField
                      select
                      name="bankName"
                      label="bankName"
                      required
                      value={formik.values.bankName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      error={
                        formik.touched.bankName &&
                        Boolean(formik.errors.bankName)
                      }
                      helperText={
                        formik.touched.bankName && formik.errors.bankName
                      }
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          marginTop: "8px",
                          color: theme.palette.secondary[100],
                        },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              backgroundColor: theme.palette.background.alt,
                              color: "inherit",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Select bankName</MenuItem>
                      {bank.map((bankName) => (
                        <MenuItem key={bankName} value={bankName}>
                          {bankName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <CustomTextField
                    name="accountNumber"
                    label="Account Number"
                    value={formik.values.accountNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errors={formik.errors}
                    touched={formik.touched}
                    type="number"
                  />
                  <Grid
                    item
                    xs={12}
                    container
                    alignItems="end"
                    justifyContent="end"
                  >
                    <DialogActions
                      sx={{ backgroundColor: theme.palette.background.alt }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleCloseForm}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="primary">
                        Submit
                      </Button>
                    </DialogActions>
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default BankAccountInformationForm;
